<script lang="ts">
import AlertDialog from '@/app/common/components/AlertDialog.vue';
import { BaseAlert } from '@/interface/BaseAlert.interface';
import { ComplaintAirlineInfo, ComplaintDepartmentInfo, ComplaintInfo } from '@/interface/Complaint.interface';
import { UserInfo } from '@/interface/UserInfo.interface';
import { masterService, userService } from '@/services/serviceProvider';
import { ref } from 'vue';

export default {
    components: {
        AlertDialog
    },
    props: {
        modelValue: {
            type: Boolean,
            default: false,
        },
        header: {
            type: String,
            default: 't-action-send-to-airline'
        },
        cancelText: {
            type: String,
            default: 't-cancel'
        },
        confirmText: {
            type: String,
            default: 't-complaint-submit'
        },
        complaint: {
            type: Object,
            default: {}
        },
        disabled: {
            type: Boolean,
            default: false
        }
    },
    emits: ['update:modelValue', 'onConfirm', 'onCancel'],
    setup() {
        const sendAirlineForm = ref();
        return { sendAirlineForm };
    },
    data() {
        return {
            request: this.complaint as ComplaintInfo,
            externalUsername: undefined as string | undefined,
            airlineOptions: [] as UserInfo[],
            failAlert: {
                display: false,
                success: false
            } as BaseAlert,
            rules: {
                required: (message: any) => {
                    return (v: any) => !!v || message;
                },
            }
        }
    },
    computed: {
        dialogShow: {
            get() {
                if (this.modelValue === true) {
                    this.getExternalUser();
                    this.externalUsername = this.activeDepartment?.external?.externalDepartment;
                }
                return this.modelValue || false
            },
            set(newValue: boolean) {
                this.$emit('update:modelValue', newValue);
            }
        },
        currentDpm() {
            return userService.currentDepartment();
        },
        isMainDpm(): boolean {
            return this.request.relatedMainDepartment?.departmentCode === userService.currentDepartment();
        },
        isSubDpm(): boolean {
            const dpmCodes = this.request.relatedSubDepartments?.map(o => o.departmentCode);
            const currentDpm = userService.currentDepartment();
            if (dpmCodes && currentDpm) return dpmCodes.includes(currentDpm);
            return false;
        },
        activeDepartment: {
            get() {
                if (this.isMainDpm) {
                    return this.request.relatedMainDepartment;
                } else if (this.isSubDpm) {
                    return this.request.relatedSubDepartments?.find(s => s.departmentCode === this.currentDpm);
                }
                return undefined;
            },
            set(newValue: ComplaintDepartmentInfo) {
                if (this.isMainDpm) {
                    this.request.relatedMainDepartment = newValue;
                } else if (this.isSubDpm) {
                    let sub = this.request.relatedSubDepartments?.find(s => s.departmentCode === this.currentDpm);
                    if (sub) sub = newValue;
                }
            }
        },
        alreadySentAirlines(): boolean {
            if (this.isMainDpm) {
                const selected = this.request.relatedSubDepartments?.filter(f => f.external?.externalDepartment !== undefined).map(o => o.external?.externalDepartment);
                if (selected && selected.length > 0) {
                    return selected.includes(this.externalUsername);
                }
            } else if (this.isSubDpm) {
                const airline = this.request.relatedMainDepartment?.external?.externalDepartment;
                if (airline) return airline === this.externalUsername;
                const selected = this.request.relatedSubDepartments?.filter(f => f.departmentCode !== this.currentDpm 
                    && f.external?.externalDepartment !== undefined)
                        .map(o => o.external?.externalDepartment);
                if (selected && selected.length > 0) {
                    return selected.includes(this.externalUsername);
                }
            }
            return false;
        }
    },
    methods: {
        async getExternalUser() {
            try {
                const res = await masterService.getExternalUsers();
                if (res && res.status === 200) {
                    let data = res.data as UserInfo[];
                    let currentDpm = userService.currentDepartment();
                    if (currentDpm) {
                        data = data.filter(o => o.departments?.includes(currentDpm))
                    }
                    data.forEach((o: UserInfo) => {
                        o.userDisplay = `${o.userDesc} ${o.email}`;
                    })
                    this.airlineOptions = data;
                }
            } catch (e) {
            }
        },
        async confirm() {
            const valid = await this.sendAirlineForm.validate();
            if (valid.valid) {
                if (!this.alreadySentAirlines) {
                    this.dialogShow = false;
                    if (this.activeDepartment) {
                        this.activeDepartment.external = {
                            externalDepartment: this.externalUsername,
                            externalDepartmentName: this.airlineOptions.find(o => o.username === this.externalUsername)?.userDesc,
                            externalDepartmentEmail: this.airlineOptions.find(o => o.username === this.externalUsername)?.email,
                        } as ComplaintAirlineInfo
                    }
                    this.$emit('onConfirm', this.request);
                } else {
                    this.failAlert.messageTitle = this.$t('t-action-send-to-airline-duplicate');
                    this.failAlert.display = true;
                }
            }
        },
        cancel() {
            this.dialogShow = false;
            this.$emit('onCancel')
        },
    }
}
</script>

<template>
    <v-dialog v-model="dialogShow" width="80%" :close-on-back="false" persistent>
        <v-form ref="sendAirlineForm" class="admin-form app-form-input" validate-on="submit lazy"
            @submit.prevent="confirm">
            <v-card class="dialog-card">
                <v-toolbar density="compact">
                    <v-toolbar-title>{{ $t(header) }}</v-toolbar-title>
                    <v-spacer></v-spacer>
                    <v-toolbar-items>
                        <v-btn icon="mdi-close" @click="dialogShow = false"></v-btn>
                    </v-toolbar-items>
                </v-toolbar>
                <v-divider class="mx-4"></v-divider>
                <v-card-text>
                    <div class="text-label">{{ $t('t-complaint-airline') }}</div>
                    <v-select v-model="externalUsername" class="hide-float-label" variant="outlined"
                        :label="$t('t-please-select')" hide-details="auto" :items="airlineOptions" item-value="username"
                        item-title="userDisplay" density="comfortable" :disabled="disabled"
                        :rules="[rules.required(`${$t('t-please-select')}${$t('t-complaint-airline')}`)]">
                        <template v-slot:item="{ props, item }">
                            <v-list-item v-bind="props" height="2000">
                                <template v-slot:title="{ title }">
                                    <!-- <v-chip class="app-chip mr-2" color="primary" v-for="d in item.raw.departments">
                                        {{ d }}
                                    </v-chip> -->
                                    {{ title }}
                                </template>
                            </v-list-item>
                        </template>
                        <template v-slot:selection="{ item }">
                            <div>{{ item.raw.userDisplay }}</div>
                        </template>
                    </v-select>
                    <v-alert v-if="disabled" density="compact" class="mt-2" color="primary" variant="tonal" border="start">
                        <div class="d-flex align-center">
                            <v-icon icon="mdi mdi-alert-circle-outline" class="mr-2"></v-icon>
                            {{ $t('t-action-send-to-airline-again-message') }}
                        </div>
                    </v-alert>
                </v-card-text>
                <v-card-actions>
                    <v-container class="mx-0">
                        <div class="d-flex justify-end">
                            <v-btn @click="cancel" variant="outlined" color="primary" size="large">
                                {{ $t(cancelText) }}
                            </v-btn>
                            <v-btn @click="confirm" variant="flat" color="primary" size="large">
                                {{ $t(confirmText) }}
                            </v-btn>
                        </div>
                    </v-container>
                </v-card-actions>
            </v-card>
        </v-form>
    </v-dialog>
    <!-- Fail -->
    <AlertDialog v-model="failAlert.display" :header="failAlert.header" :title="failAlert.messageTitle"
        :text="failAlert.messageText" :success="false">
    </AlertDialog>
</template>